<template>
  <div>
    <!-- Date range picker -->
    <calendar-monthly-bar
        v-model="selectedMonths"
        @input="onMonthSelected"
        :max-options-size="6"
        :future-options-size="3"
    />
    <b-row class="align-items-end">
      <!-- Pick Building -->
      <b-col cols="4">
        <b-form-group :label="msg('Pick Building (Not active buildings are not shown)')" label-for="building-select">
          <building-select-box only-active id="building-select" v-model="buildings" multiple clearable
                               @input="refresh"/>
        </b-form-group>
      </b-col>

    </b-row>
    <!-- Summary Cards -->
    <b-row class="mt-1">
      <b-col cols="12" lg="4" class="">
        <statistic-card-horizontal
            icon="fas fa-euro-sign"
            :statistic="stats.totalRevenue"
            post-fix=" €"
            formatNumber
            :loading="false"
            statistic-title="Total revenue"
            icon-type="fontawesome"
            color="success"
        />
      </b-col>
      <b-col cols="12" lg="2" class="">
        <statistic-card-horizontal
            icon="fas fa-boxes"
            :statistic="stats.totalUnits"
            formatNumber
            :loading="false"
            statistic-title="Total units"
            icon-type="fontawesome"
            color="primary"
        />
      </b-col>
      <b-col cols="12" lg="2" class="">
        <statistic-card-horizontal
            icon="fas fa-money-bill-wave"
            :statistic="stats.statusCounts.PAID!=null? stats.statusCounts.PAID : 0"
            formatNumber
            :loading="false"
            statistic-title="Paid"
            icon-type="fontawesome"
            color="success"
        />
      </b-col>
      <b-col cols="12" lg="2" class="">
        <statistic-card-horizontal
            icon="fas fa-user-check"
            :statistic="stats.statusCounts.REQUESTED!=null? stats.statusCounts.REQUESTED : 0"
            formatNumber
            :loading="false"
            statistic-title="Requested"
            icon-type="fontawesome"
            color="warning"
        />
      </b-col>
      <b-col cols="12" lg="2" class="">
        <statistic-card-horizontal
            icon="fas fa-trash"
            :statistic="stats.statusCounts.CANCELED!=null? stats.statusCounts.CANCELED : 0"
            formatNumber
            :loading="false"
            :statistic-title="$t('Cancelled')"
            icon-type="fontawesome"
            color="danger"
        />
      </b-col>
    </b-row>

    <!-- Status Breakdown & Units Sold by Product Charts -->
    <b-row class="mt-1">
      <b-col cols="12" md="4">
        <b-card>
          <h5>{{ $t('Status breakdown') }}</h5>
          <status-donut-chart
              :data="statusChartData"
              :loading="false"
          />
        </b-card>
      </b-col>

      <b-col cols="12" md="8">
        <b-card>
          <h5>{{ $t('Units Sold by Product') }}</h5>
          <vue-apex-charts
              type="bar"
              height="350"
              :options="unitsChartOptions"
              :series="unitsSeries"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- Top Products Table -->
    <b-row class="">
      <b-col cols="12">
        <b-card>
          <h5>Top 5 {{ $t('products') }}</h5>
          <b-table :items="stats.topProducts" :fields="topFields" small responsive>
            <template #cell(imageUri)="{ item }">
              <b-img :src="item.imageUri" thumbnail fluid style="width:100px"/>
            </template>
            <template #cell(unitsSold)="{ value }">
              {{ value }}
            </template>
            <template #cell(revenue)="{ value }">
              €{{ value.toFixed(2) }}
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
    <!-- Top Products Table -->
    <b-row class="">
      <b-col cols="12">
        <b-card>
          <h5>{{ $t('Units Requested by Product') }}</h5>
          <vue-apex-charts
              type="bar"
              height="350"
              :options="requestedChartOptions"
              :series="requestedSeries"
          />
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import CalendarMonthlyBar from '@/components/widgets/calendar/CalendarMonthlyBar.vue';
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue';
import VueApexCharts from 'vue-apexcharts';
import StatusDonutChart from "@/components/chart/realEstates/contract/StatusDonutChart.vue";
import {mapActions, mapGetters} from "vuex";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker.vue";
import BuildingSelectBox from "@/components/components/picker/buildingSelectBox.vue";

const statsTemplate = () => ({
  totalRevenue: 0,
  totalUnits: 0,
  statusCounts: {REQUESTED: 0, PAID: 0, CANCELED: 0},
  topProducts: [],
  unitsSoldByProduct: {},
  unitsRequestedByProduct: {}
});
export default {
  name: 'guestFeatureDashboard',
  components: {
    BuildingSelectBox,
    CalendarMonthlyBar,
    StatisticCardHorizontal,
    StatusDonutChart,
    VueApexCharts
  },
  data() {
    return {
      buildings: [],

      selectedMonths: {start: null, end: null},
      stats: statsTemplate(),

    }
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkMode']),
    topFields() {
      return [
        {key: 'imageUri', label: ''},
        {key: 'name', label: this.$t('feature')},
        {key: 'unitsSold', label: this.$t('quantity')},
        {key: 'revenue', label: this.$t('revenue')}
      ]
    },
    statusChartData() {
      return Object.entries(this.stats.statusCounts).map(([name, value]) => ({name, value}));
    },
    // NEW: requested units series and options
    requestedSeries() {
      return [{name: 'Units in open requests', data: Object.values(this.stats.unitsRequestedByProduct)}];

    },
    requestedChartOptions() {
      let $this = this;
      return {
        chart: {
          id: 'requested-bar-chart',
          foreColor: $this.isDarkMode ? '#fafafa' : '#373d3f'
        },
        xaxis: {
          categories: Object.keys(this.stats.unitsRequestedByProduct)
        },
        colors: ['#c08239'],  // all bars will be this red
        plotOptions: {
          bar: {
            distributed: true  // if you want each bar individually colored; you can omit if you want uniform red
          }
        }
      };
    },
    unitsSeries() {
      return [{name: 'Units Sold', data: Object.values(this.stats.unitsSoldByProduct)}];
    },
    unitsChartOptions() {
      let $this = this;
      return {
        chart: {
          id: 'units-bar-chart',
          foreColor: $this.isDarkMode ? '#fafafa' : '#373d3f'
        },
        xaxis: {
          categories: Object.keys(this.stats.unitsSoldByProduct)
        },
        colors: ['#c2aa8e'],  // all bars will be this red
        plotOptions: {
          bar: {
            distributed: true  // if you want each bar individually colored; you can omit if you want uniform red
          }
        }
      };
    }

  },
  created() {
    this.$root.$on('crm::branch-updated', this.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.refresh);
  },
  methods: {
    ...mapActions('guestFeature', ['fetchGuestFeatureDashboardStats']),

    onMonthSelected(month) {
      this.selectedMonths = month;
      this.refresh();
    },

    refresh() {
      let $this = this;
      this.stats.loading = true;
      const {start, end} = this.selectedMonths;
      let buildings = this.buildings;
      this.fetchGuestFeatureDashboardStats({start, end, buildings})
          .then(stats => {
            $this.stats = stats;
          })
          .finally(() => {
            $this.stats.loading = true;
          });
    },
  }
};
</script>

<style scoped>
h5 {
  font-weight: 600;
}
</style>
